import {
  ArrayExt,
  ChoiceDisplay,
  MaybeExt,
  MultichoiceDisplay,
} from '@castoredc/castor_prose';
import {
  convertProseDateToJsDate,
  convertProseDateTimeToJsDate,
  convertProseTimeToJsDate,
} from './proseDateUtils';
import type {
  FieldData,
  Field,
  InputField,
  ViewField,
  FieldChoice,
  FieldMultichoice,
  FieldNumber,
  FieldSignature,
  NumberNrsPresentation,
  NumberVasPresentation,
  NumberDropdownPresentation,
  Template,
  Value,
  FieldValue,
  TemplateArgValue,
} from '@castoredc/castor_prose';
import type { DbCalculationsData, DynamicT } from '@hub/common';

export const isProseInputField = (definition: Field): boolean =>
  definition.kind?.$case === 'inputField';

export const isProseChoiceField = (definition: Field): boolean =>
  isProseInputField(definition) &&
  definition.kind?.value.kind?.$case === 'fieldChoice';

export const isProseMultichoiceField = (definition: Field): boolean =>
  isProseInputField(definition) &&
  definition.kind?.value.kind?.$case === 'fieldMultichoice';

export const isProseNumberField = (definition: Field): boolean =>
  isProseInputField(definition) &&
  definition.kind?.value.kind?.$case === 'fieldNumber';

export const getProseInputField = (definition: Field): InputField => {
  if (definition.kind?.$case !== 'inputField') {
    throw new Error(
      `[Assert failed] Field ${definition.name} is not an InputField`
    );
  }

  return definition.kind.value;
};

const getProseViewField = (definition: Field): ViewField => {
  if (definition.kind?.$case !== 'viewField') {
    throw new Error(
      `[Assert failed] Field ${definition.name} is not a ViewField`
    );
  }

  return definition.kind.value;
};

export const getFieldLabelText = (
  definition: Field,
  dynamicT: DynamicT
): string => dynamicT(definition.i18nLabelKey);

export const getFieldHelpText = (
  definition: Field,
  dynamicT: DynamicT
): string => dynamicT(getProseInputField(definition).i18nHelpKey);

export const isFieldRequired = (definition: Field): boolean => {
  if (definition.kind?.$case === 'inputField') {
    return definition.kind.value.required;
  }

  return false;
};

export const isFieldEditable = (definition: Field): boolean => {
  if (definition.kind?.$case === 'inputField') {
    return definition.kind.value.editable;
  }

  return false;
};

export const getProseFieldNumberPresentationDisplay = (definition: Field) => {
  const fieldNumber = getProseFieldNumber(definition);

  if (!fieldNumber.presentation) {
    throw new Error(
      `[Assert failed] presentation is not defined for ${definition.name}`
    );
  }

  if (!fieldNumber.presentation.display) {
    throw new Error(
      `[Assert failed] presentation.display is not defined for ${definition.name}`
    );
  }

  return fieldNumber.presentation.display;
};

export const getProseFieldChoice = (definition: Field): FieldChoice => {
  const inputField = getProseInputField(definition);

  if (inputField.kind?.$case === 'fieldChoice') {
    return inputField.kind.value;
  }

  throw new Error(
    `[Assert failed] Field ${definition.name} is not a FieldChoice`
  );
};

export const getProseFieldMultichoice = (
  definition: Field
): FieldMultichoice => {
  const inputField = getProseInputField(definition);

  if (inputField.kind?.$case === 'fieldMultichoice') {
    return inputField.kind.value;
  }

  throw new Error(
    `[Assert failed] Field ${definition.name} is not a FieldMultichoice`
  );
};

export const getProseFieldNumber = (definition: Field): FieldNumber => {
  const inputField = getProseInputField(definition);

  if (inputField.kind?.$case === 'fieldNumber') {
    return inputField.kind.value;
  }

  throw new Error(
    `[Assert failed] Field ${definition.name} is not a FieldNumber`
  );
};

export const getProseFieldSignature = (definition: Field): FieldSignature => {
  const inputField = getProseInputField(definition);

  if (inputField.kind?.$case === 'fieldSignature') {
    return inputField.kind.value;
  }

  throw new Error(
    `[Assert failed] Field ${definition.name} is not a FieldSignature`
  );
};

export const getProseFieldTemplate = (definition: Field): Template => {
  const viewField = getProseViewField(definition);

  if (viewField.kind?.$case === 'template') {
    return viewField.kind.value;
  }

  throw new Error(`[Assert failed] Field ${definition.name} is not a Template`);
};

export const resolveTemplateArgValue = (
  argValue: TemplateArgValue,
  fieldsData: FieldData,
  calculationsData: DbCalculationsData,
  locale = 'en-US'
): string => {
  if (!argValue.kind) {
    return '';
  }

  switch (argValue.kind.$case) {
    case 'field': {
      const fieldName = argValue.kind.value.name;
      const fieldValue = fieldsData.values[fieldName];

      if (!fieldValue) {
        return '';
      }

      return formatFieldValueForDisplay(fieldValue, locale);
    }

    case 'calculation': {
      const calculationName = argValue.kind.value.name;
      const calculationData = calculationsData?.values[calculationName];

      if (!calculationData?.kind) {
        return '';
      }

      return formatValueForDisplay(calculationData, locale);
    }

    default:
      return '';
  }
};

const formatFieldValueForDisplay = (
  fieldValue: FieldValue,
  locale = 'en-US'
): string => {
  if (!fieldValue.kind) {
    return '';
  }

  switch (fieldValue.kind.$case) {
    case 'value':
      return formatValueForDisplay(fieldValue.kind.value, locale);

    case 'unrepresentableValue':
      return '[unrepresentable value]';

    default:
      return '';
  }
};

// 'true' / 'false' / '[unrepresentable value]' / '[unsupported]' below are
// debug-grade fallbacks for value kinds rarely surfaced in templates (raw
// booleans, schema mismatches). They are intentionally untranslated: routing
// dynamicT through this pure formatter is API churn for cold paths. Localize
// values via PROSE option labels (enums) or template-arg formatting (numbers,
// dates) — the typical paths.
export const formatValueForDisplay = (value: Value, locale: string): string => {
  if (!value.kind) {
    return '';
  }

  switch (value.kind.$case) {
    case 'boolean':
      return value.kind.value ? 'true' : 'false';

    case 'float64':
      return value.kind.value.toLocaleString(locale);

    case 'str':
      return value.kind.value;

    case 'maybe': {
      const inner = MaybeExt.as(value.kind.value);
      return inner ? formatValueForDisplay(inner, locale) : '';
    }

    case 'array': {
      const arr = value.kind.value;
      const strs = ArrayExt.as(arr, 'str')?.items;
      if (strs) return strs.join(', ');
      const nums = ArrayExt.as(arr, 'float64')?.items;
      if (nums) return nums.map(n => n.toLocaleString(locale)).join(', ');
      const bools = ArrayExt.as(arr, 'boolean')?.items;
      if (bools) return bools.map(b => (b ? 'true' : 'false')).join(', ');
      const maybes = ArrayExt.as(arr, 'maybe')?.items;
      if (maybes) {
        return maybes
          .map(m => {
            const inner = MaybeExt.as(m);
            return inner ? formatValueForDisplay(inner, locale) : '';
          })
          .filter(Boolean)
          .join(', ');
      }
      // Unknown inner kind (e.g. nested array-of-array) — surface loudly
      // rather than silently emitting an empty string.
      return '[unsupported]';
    }

    case 'date':
      return convertProseDateToJsDate(value.kind.value).toLocaleDateString(
        locale
      );

    case 'dateTime':
      return convertProseDateTimeToJsDate(value.kind.value).toLocaleString(
        locale
      );

    case 'time':
      return convertProseTimeToJsDate(value.kind.value).toLocaleTimeString(
        locale,
        { hour: '2-digit', minute: '2-digit' }
      );

    default:
      return '';
  }
};

export const isProseNumberVas = (definition: Field): boolean => {
  if (!isProseNumberField(definition)) {
    return false;
  }

  const fieldNumber = getProseFieldNumber(definition);
  return fieldNumber.presentation?.display?.$case === 'vas';
};

export const getProseNumberVasPresentation = (
  definition: Field
): NumberVasPresentation => {
  const display = getProseFieldNumberPresentationDisplay(definition);

  if (display.$case !== 'vas') {
    throw new Error('[Assert failed] Unable to obtain vas presentation');
  }

  return display.value;
};

export const isProseNumberNrs = (definition: Field): boolean => {
  if (!isProseNumberField(definition)) {
    return false;
  }

  const fieldNumber = getProseFieldNumber(definition);
  return fieldNumber.presentation?.display?.$case === 'nrs';
};

export const getProseNumberNrsPresentation = (
  definition: Field
): NumberNrsPresentation => {
  const display = getProseFieldNumberPresentationDisplay(definition);

  if (display.$case !== 'nrs') {
    throw new Error('[Assert failed] Unable to obtain nrs presentation');
  }

  return display.value;
};

export const isProseNumberDropdown = (definition: Field): boolean => {
  if (!isProseNumberField(definition)) {
    return false;
  }

  const fieldNumber = getProseFieldNumber(definition);
  return fieldNumber.presentation?.display?.$case === 'dropdown';
};

export const getProseNumberDropdownPresentation = (
  definition: Field
): NumberDropdownPresentation => {
  const display = getProseFieldNumberPresentationDisplay(definition);

  if (display.$case !== 'dropdown') {
    throw new Error('[Assert failed] Unable to obtain dropdown presentation');
  }

  return display.value;
};

export const isProseChoiceRadio = (definition: Field): boolean =>
  isProseChoiceField(definition) &&
  getProseFieldChoice(definition).presentation?.display === ChoiceDisplay.RADIO;

export const isProseChoiceCheckbox = (definition: Field): boolean =>
  isProseChoiceField(definition) &&
  getProseFieldChoice(definition).presentation?.display ===
    ChoiceDisplay.CHECKBOX;

export const isProseMultichoiceRadio = (definition: Field): boolean =>
  isProseMultichoiceField(definition) &&
  getProseFieldMultichoice(definition).presentation?.display ===
    MultichoiceDisplay.RADIO;

export const isProseMultichoiceCheckbox = (definition: Field): boolean =>
  isProseMultichoiceField(definition) &&
  getProseFieldMultichoice(definition).presentation?.display ===
    MultichoiceDisplay.CHECKBOX;

// TODO: In the future question counting ("x of y" and progress calculation) should happen in PROSE and
// this util could be removed.
// Counts input fields only; view fields such as templates are not questions.
export const isFieldCountableAsQuestion = (definition: Field) =>
  isProseInputField(definition);

const uses12HourFormatCache = new Map<string, boolean>();

export const uses12HourFormat = (locale: string): boolean => {
  const cached = uses12HourFormatCache.get(locale);
  if (cached !== undefined) return cached;

  const formatter = new Intl.DateTimeFormat(locale, { hour: 'numeric' });
  const options = formatter.resolvedOptions();
  const result = options.hourCycle === 'h12' || options.hourCycle === 'h11';
  uses12HourFormatCache.set(locale, result);
  return result;
};
